import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./TripScheduler.css";
import { supabase } from "../supabase";
import { withRetry } from "../utils/supabaseErrorHandler";
import TripPricingSettings from "../components/TripPricingSettings";

const formatPrice = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(3);
};

const parseNumber = (value) => {
  const num = Number(String(value ?? "").replace(",", "."));
  return Number.isNaN(num) ? 0 : num;
};

const formatMoney = (value) =>
  parseNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const computeTripProductTotals = (productLines) => {
  const lines = Array.isArray(productLines) ? productLines : [];
  return lines.reduce(
    (acc, item) => {
      const qty = parseNumber(item.quantity ?? item.quantite ?? 0);
      const sell = parseNumber(item.price);
      const buy = parseNumber(item.prix_usine ?? item.prixUsine ?? 0);
      const revenue = qty * sell;
      const cost = qty * buy;
      return {
        quantity: acc.quantity + qty,
        revenue: acc.revenue + revenue,
        cost: acc.cost + cost,
        margin: acc.margin + (revenue - cost),
      };
    },
    { quantity: 0, revenue: 0, cost: 0, margin: 0 }
  );
};

const TripScheduler = () => {
  const [trips, setTrips] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [filteredTrucks, setFilteredTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [expandedTrips, setExpandedTrips] = useState({});
  const [scheduleProducts, setScheduleProducts] = useState([]);
  const [scheduleDestinations, setScheduleDestinations] = useState([]);
  const [schedulePrices, setSchedulePrices] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [editSelectedProducts, setEditSelectedProducts] = useState([]);
  const [showEditAllProducts, setShowEditAllProducts] = useState(false);
  const [showPricingSettings, setShowPricingSettings] = useState(false);
  const [formData, setFormData] = useState({
    truckId: "",
    destination: "",
    date: "",
    cargo: "",
    description: "",
    status: "scheduled",
    color: "#1E3A8A",
  });
  const [assignFormData, setAssignFormData] = useState({
    tripId: "",
    truckId: "",
  });
  const [editFormData, setEditFormData] = useState({
    id: "",
    truckId: "",
    destination: "",
    date: "",
    cargo: "",
    description: "",
    status: "scheduled",
    color: "#1E3A8A",
  });
  const [truckSearch, setTruckSearch] = useState("");
  const [showAddTripModal, setShowAddTripModal] = useState(false);
  const [showAssignTruckModal, setShowAssignTruckModal] = useState(false);
  const [showEditTripModal, setShowEditTripModal] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [showTruckList, setShowTruckList] = useState(false);
  const [page, setPage] = useState(0);
  const [maxDate, setMaxDate] = useState(null);
  const [draggedTripId, setDraggedTripId] = useState(null);
  const [dropTargetDate, setDropTargetDate] = useState(null);
  const [movingTripId, setMovingTripId] = useState(null);
  const [tripSearchQuery, setTripSearchQuery] = useState("");
  const [unassignedOnlyFilter, setUnassignedOnlyFilter] = useState(false);
  const truckListRef = useRef(null);
  const suppressDayClickRef = useRef(false);

  const colorOptions = [
    { value: "#B91C1C", label: "Rouge" },
    { value: "#C2410C", label: "Orange" },
    { value: "#B45309", label: "Ambre" },
    { value: "#BE185D", label: "Rose" },
    { value: "#1E3A8A", label: "Bleu" },
    { value: "#4338CA", label: "Indigo" },
    { value: "#0E7490", label: "Turquoise" },
    { value: "#15803D", label: "Vert" },
    { value: "#0F766E", label: "Sarcelle" },
    { value: "#4D7C0F", label: "Olive" },
    { value: "#6B21A8", label: "Violet" },
    { value: "#78350F", label: "Marron" },
    { value: "#1F2937", label: "Noir" },
    { value: "#374151", label: "Gris" },
  ];

  const ColorPicker = ({ value, onSelect }) => (
    <div className="trip-scheduler-color-picker">
      {colorOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`trip-scheduler-color-swatch ${value === option.value ? "selected" : ""}`}
          style={{ backgroundColor: option.value }}
          onClick={() => onSelect(option.value)}
          title={option.label}
          aria-label={option.label}
        />
      ))}
    </div>
  );

  const fetchPricingCatalog = useCallback(async () => {
    try {
      const [productsRes, destinationsRes, pricesRes] = await Promise.all([
        supabase.from("schedule_products").select("*").order("name", { ascending: true }),
        supabase.from("schedule_destinations").select("*").order("name", { ascending: true }),
        supabase.from("schedule_destination_prices").select("*"),
      ]);
      if (!productsRes.error) setScheduleProducts(productsRes.data || []);
      if (!destinationsRes.error) setScheduleDestinations(destinationsRes.data || []);
      if (!pricesRes.error) setSchedulePrices(pricesRes.data || []);
    } catch (err) {
      console.error("Error fetching pricing catalog:", err);
    }
  }, []);

  const getProductsForDestination = (destinationName) => {
    if (!destinationName) return [];
    const destination = scheduleDestinations.find(
      (d) => d.name.toLowerCase() === destinationName.toLowerCase()
    );
    if (!destination) return [];
    return schedulePrices
      .filter((price) => price.destination_id === destination.id)
      .map((price) => {
        const product = scheduleProducts.find((p) => p.id === price.product_id);
        if (!product) return null;
        return {
          productId: product.id,
          productName: product.name,
          price: Number(price.price),
          prixUsine: Number(product.prix_usine ?? 0),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.productName.localeCompare(b.productName, "fr"));
  };

  const buildProductPricesPayload = (products) =>
    products.map((item) => {
      const price = parseNumber(item.price);
      const prixUsine = parseNumber(item.prixUsine ?? 0);
      const quantity = parseNumber(item.quantity ?? 0);
      return {
        product_id: item.productId,
        product_name: item.productName,
        price,
        prix_usine: prixUsine,
        quantity,
      };
    });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: trucksData, error: trucksError } = await supabase
        .from('trucks')
        .select('id, immatriculation');
      if (trucksError) throw trucksError;
      const formattedTrucks = trucksData.map(truck => ({
        id: truck.id,
        immatriculation: truck.immatriculation || truck.id,
      }));
      setTrucks(formattedTrucks);
      setFilteredTrucks(formattedTrucks);

      const { data: tripsData, error: tripsError } = await supabase
        .from('trips')
        .select('*');
      if (tripsError) throw tripsError;

      await fetchPricingCatalog();

      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      const formattedTrips = tripsData
        .map(trip => {
          const tripDate = new Date(trip.date);
          tripDate.setUTCHours(0, 0, 0, 0);
          return {
            id: trip.id,
            truckId: trip.truck_id,
            destination: trip.destination,
            date: tripDate.toLocaleDateString('fr-FR'),
            rawDate: trip.date,
            cargo: trip.cargo,
            description: trip.description || "",
            productPrices: Array.isArray(trip.product_prices) ? trip.product_prices : [],
            status: trip.status,
            color: trip.color || "#1E3A8A",
            createdAt: new Date(trip.created_at),
          };
        })
        .filter(trip => {
          // Keep completed trips in DB for profitability; hide them from the planner
          if (trip.status === "completed" || trip.status === "canceled") return false;
          const tripDate = new Date(trip.rawDate);
          tripDate.setUTCHours(0, 0, 0, 0);
          return tripDate >= today;
        });

      setTrips(formattedTrips);

      if (formattedTrips.length > 0) {
        const futureDates = formattedTrips.map(trip => new Date(trip.rawDate));
        const maxTripDate = new Date(Math.max(...futureDates));
        maxTripDate.setUTCHours(0, 0, 0, 0);
        setMaxDate(maxTripDate);
      } else {
        setMaxDate(today);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Échec du chargement des données.");
    } finally {
      setLoading(false);
    }
  }, [fetchPricingCatalog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (truckSearch && !selectedTruck) {
      const filtered = trucks.filter((truck) =>
        truck.immatriculation.toLowerCase().includes(truckSearch.toLowerCase())
      );
      setFilteredTrucks(filtered);
      setShowTruckList(true);
    } else {
      setFilteredTrucks([]);
      setShowTruckList(false);
    }
  }, [truckSearch, trucks, selectedTruck]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (truckListRef.current && !truckListRef.current.contains(e.target) && e.target.id !== "truckSearch" && e.target.id !== "truckSearchAssign" && e.target.id !== "truckSearchEdit") {
        setShowTruckList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "destination") {
      setSelectedProducts([]);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "destination") {
      setEditSelectedProducts([]);
      setShowEditAllProducts(false);
    }
  };

  const toggleSelectedProduct = (item, isEdit = false) => {
    const setter = isEdit ? setEditSelectedProducts : setSelectedProducts;
    setter((prev) => {
      const exists = prev.some((p) => p.productId === item.productId);
      if (exists) {
        return prev.filter((p) => p.productId !== item.productId);
      }
      return [
        ...prev,
        {
          productId: item.productId,
          productName: item.productName,
          price: formatPrice(item.price),
          prixUsine: formatPrice(item.prixUsine ?? 0),
          quantity: "",
        },
      ];
    });
  };

  const updateSelectedProductPrice = (productId, priceValue, isEdit = false) => {
    const setter = isEdit ? setEditSelectedProducts : setSelectedProducts;
    setter((prev) =>
      prev.map((p) =>
        p.productId === productId ? { ...p, price: priceValue } : p
      )
    );
  };

  const updateSelectedProductQuantity = (productId, quantityValue, isEdit = false) => {
    const setter = isEdit ? setEditSelectedProducts : setSelectedProducts;
    setter((prev) =>
      prev.map((p) =>
        p.productId === productId ? { ...p, quantity: quantityValue } : p
      )
    );
  };

  const handleTruckSelect = (truck, isAssignModal = false, isEditModal = false) => {
    if (isAssignModal) {
      setAssignFormData((prev) => ({ ...prev, truckId: truck.id }));
      setTruckSearch(truck.immatriculation);
      setSelectedTruck(truck);
    } else if (isEditModal) {
      setEditFormData((prev) => ({ ...prev, truckId: truck.id }));
      setTruckSearch(truck.immatriculation);
      setSelectedTruck(truck);
    } else {
      setFormData((prev) => ({ ...prev, truckId: truck.id }));
      setTruckSearch(truck.immatriculation);
      setSelectedTruck(truck);
    }
    setShowTruckList(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!formData.destination) {
      setError("Veuillez indiquer une destination.");
      document.getElementById("destination").focus();
      return;
    }
    if (!formData.date) {
      setError("Veuillez sélectionner une date de voyage.");
      document.getElementById("date").focus();
      return;
    }
    if (!formData.cargo) {
      setError("Veuillez décrire la cargaison.");
      document.getElementById("cargo").focus();
      return;
    }
    if (selectedProducts.some((p) => parseNumber(p.quantity) <= 0)) {
      setError("Indiquez une quantité supérieure à 0 pour chaque produit sélectionné.");
      return;
    }
    const tripDate = new Date(formData.date);
    tripDate.setUTCHours(0, 0, 0, 0);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    if (tripDate < today) {
      setError("La date du voyage doit être aujourd'hui ou dans le futur.");
      document.getElementById("date").focus();
      return;
    }
    try {
      const formattedDate = tripDate.toLocaleDateString("fr-FR");
      const productPrices = buildProductPricesPayload(selectedProducts);
      const { data, error } = await withRetry(() =>
        supabase
          .from('trips')
          .insert([{
            truck_id: formData.truckId || null,
            destination: formData.destination,
            date: formData.date,
            cargo: formData.cargo,
            description: formData.description,
            product_prices: productPrices,
            status: formData.status,
            color: formData.color,
          }])
          .select()
      );
      if (error) throw error;
      const newTrip = {
        id: data[0].id,
        truckId: data[0].truck_id,
        destination: data[0].destination,
        date: formattedDate,
        rawDate: data[0].date,
        cargo: data[0].cargo,
        description: data[0].description,
        productPrices: Array.isArray(data[0].product_prices) ? data[0].product_prices : productPrices,
        status: data[0].status,
        color: data[0].color,
        createdAt: new Date(data[0].created_at),
      };
      setTrips((prev) => [...prev, newTrip]);
      const newTripDate = new Date(newTrip.rawDate);
      newTripDate.setUTCHours(0, 0, 0, 0);
      if (!maxDate || newTripDate > maxDate) {
        setMaxDate(newTripDate);
      }
      setFormData({ truckId: "", destination: "", date: "", cargo: "", description: "", status: "scheduled", color: "#1E3A8A" });
      setSelectedProducts([]);
      setTruckSearch("");
      setSelectedTruck(null);
      setShowAddTripModal(false);
      setSuccessMessage("Voyage planifié avec succès !");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error scheduling trip:", err);
      setError(
        err?.message?.includes("product_prices")
          ? "Colonne product_prices manquante. Exécutez la migration SQL dans Supabase."
          : "Erreur lors de la planification du voyage."
      );
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!editFormData.destination) {
      setError("Veuillez indiquer une destination.");
      document.getElementById("editDestination").focus();
      return;
    }
    if (!editFormData.date) {
      setError("Veuillez sélectionner une date de voyage.");
      document.getElementById("editDate").focus();
      return;
    }
    if (!editFormData.cargo) {
      setError("Veuillez décrire la cargaison.");
      document.getElementById("editCargo").focus();
      return;
    }
    if (editSelectedProducts.some((p) => parseNumber(p.quantity) <= 0)) {
      setError("Indiquez une quantité supérieure à 0 pour chaque produit sélectionné.");
      return;
    }
    const tripDate = new Date(editFormData.date);
    tripDate.setUTCHours(0, 0, 0, 0);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    if (tripDate < today) {
      setError("La date du voyage doit être aujourd'hui ou dans le futur.");
      document.getElementById("editDate").focus();
      return;
    }
    try {
      const formattedDate = tripDate.toLocaleDateString("fr-FR");
      const productPrices = buildProductPricesPayload(editSelectedProducts);
      const { error } = await withRetry(() =>
        supabase
          .from('trips')
          .update({
            truck_id: editFormData.truckId || null,
            destination: editFormData.destination,
            date: editFormData.date,
            cargo: editFormData.cargo,
            description: editFormData.description,
            product_prices: productPrices,
            status: editFormData.status,
            color: editFormData.color,
          })
          .eq('id', editFormData.id)
      );
      if (error) throw error;
      setTrips((prev) =>
        prev.map((t) =>
          t.id === editFormData.id
            ? {
                ...t,
                truckId: editFormData.truckId,
                destination: editFormData.destination,
                date: formattedDate,
                rawDate: editFormData.date,
                cargo: editFormData.cargo,
                description: editFormData.description,
                productPrices,
                status: editFormData.status,
                color: editFormData.color,
              }
            : t
        )
      );
      const updatedTrips = trips.map(t =>
        t.id === editFormData.id
          ? { ...t, rawDate: editFormData.date }
          : t
      );
      const futureDates = updatedTrips
        .map(trip => {
          const date = new Date(trip.rawDate);
          date.setUTCHours(0, 0, 0, 0);
          return date;
        })
        .filter(date => date >= today);
      const newMaxDate = futureDates.length > 0 ? new Date(Math.max(...futureDates)) : today;
      newMaxDate.setUTCHours(0, 0, 0, 0);
      setMaxDate(newMaxDate);
      setEditFormData({ id: "", truckId: "", destination: "", date: "", cargo: "", description: "", status: "scheduled", color: "#1E3A8A" });
      setEditSelectedProducts([]);
      setShowEditAllProducts(false);
      setTruckSearch("");
      setSelectedTruck(null);
      setShowEditTripModal(false);
      setSuccessMessage("Voyage modifié avec succès !");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error editing trip:", err);
      setError(
        err?.message?.includes("product_prices")
          ? "Colonne product_prices manquante. Exécutez la migration SQL dans Supabase."
          : "Erreur lors de la modification du voyage."
      );
    }
  };

  const handleAssignTruck = async (e) => {
    e.preventDefault();
    setError(null);
    if (!assignFormData.truckId) {
      setError("Veuillez sélectionner un camion.");
      document.getElementById("assignTruckSelect").focus();
      return;
    }
    try {
      const { error } = await withRetry(() =>
        supabase
          .from('trips')
          .update({ truck_id: assignFormData.truckId })
          .eq('id', assignFormData.tripId)
      );
      if (error) throw error;
      setTrips((prev) =>
        prev.map((t) =>
          t.id === assignFormData.tripId ? { ...t, truckId: assignFormData.truckId } : t
        )
      );
      setAssignFormData({ tripId: "", truckId: "" });
      setTruckSearch("");
      setSelectedTruck(null);
      setShowAssignTruckModal(false);
      setSuccessMessage("Camion assigné avec succès !");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error assigning truck:", err);
      setError("Erreur lors de l'assignation du camion.");
    }
  };

  const handleCancel = async (tripId) => {
    if (window.confirm("Êtes-vous sûr de vouloir annuler ce voyage ?")) {
      try {
        const { error } = await withRetry(() =>
          supabase
            .from('trips')
            .delete()
            .eq('id', tripId)
        );
        if (error) throw error;
        setTrips((prev) => prev.filter((t) => t.id !== tripId));
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        if (trips.length > 1) {
          const remainingTrips = trips.filter(t => t.id !== tripId);
          const futureDates = remainingTrips
            .map(trip => new Date(trip.rawDate))
            .filter(date => {
              date.setUTCHours(0, 0, 0, 0);
              return date >= today;
            });
          const newMaxDate = futureDates.length > 0 ? new Date(Math.max(...futureDates)) : today;
          newMaxDate.setUTCHours(0, 0, 0, 0);
          setMaxDate(newMaxDate);
        } else {
          setMaxDate(today);
        }
        setSuccessMessage("Voyage annulé et supprimé avec succès !");
        setTimeout(() => setSuccessMessage(""), 3000);
      } catch (err) {
        console.error("Error deleting trip:", err);
        setError("Erreur lors de la suppression du voyage.");
      }
    }
  };

  const handleComplete = async (tripId) => {
    if (window.confirm("Êtes-vous sûr de vouloir marquer ce voyage comme terminé ?")) {
      try {
        const { error } = await withRetry(() =>
          supabase
            .from('trips')
            .update({ status: "completed" })
            .eq('id', tripId)
        );
        if (error) throw error;
        // Archived in DB (status=completed); remove from planner view only
        setTrips((prev) => prev.filter((t) => t.id !== tripId));
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        if (trips.length > 1) {
          const remainingTrips = trips.filter(t => t.id !== tripId);
          const futureDates = remainingTrips
            .map(trip => new Date(trip.rawDate))
            .filter(date => {
              date.setUTCHours(0, 0, 0, 0);
              return date >= today;
            });
          const newMaxDate = futureDates.length > 0 ? new Date(Math.max(...futureDates)) : today;
          newMaxDate.setUTCHours(0, 0, 0, 0);
          setMaxDate(newMaxDate);
        } else {
          setMaxDate(today);
        }
        setSuccessMessage("Voyage terminé et archivé avec succès !");
        setTimeout(() => setSuccessMessage(""), 3000);
      } catch (err) {
        console.error("Error completing trip:", err);
        setError("Erreur lors de l'archivage du voyage.");
      }
    }
  };

  const openAddTripModal = (date = null) => {
    const formattedDate = date || new Date().toISOString().split('T')[0];
    setFormData({
      truckId: "",
      destination: "",
      date: formattedDate,
      cargo: "",
      description: "",
      status: "scheduled",
      color: "#1E3A8A",
    });
    setSelectedProducts([]);
    setTruckSearch("");
    setSelectedTruck(null);
    setError(null);
    setShowAddTripModal(true);
  };

  const openAssignTruckModal = (tripId) => {
    setAssignFormData({ tripId, truckId: "" });
    setTruckSearch("");
    setSelectedTruck(null);
    setError(null);
    setShowAssignTruckModal(true);
  };

  const openEditTripModal = (trip) => {
    setEditFormData({
      id: trip.id,
      truckId: trip.truckId || "",
      destination: trip.destination,
      date: trip.rawDate.split('T')[0],
      cargo: trip.cargo,
      description: trip.description || "",
      status: trip.status,
      color: trip.color || "#1E3A8A",
    });
    const savedProducts = (trip.productPrices || []).map((item) => {
      const catalog = scheduleProducts.find((p) => p.id === item.product_id);
      return {
        productId: item.product_id,
        productName: item.product_name,
        price: formatPrice(item.price),
        prixUsine: formatPrice(
          item.prix_usine != null ? item.prix_usine : (catalog?.prix_usine ?? 0)
        ),
        quantity: item.quantity != null && item.quantity !== "" ? String(item.quantity) : "",
      };
    });
    setEditSelectedProducts(savedProducts);
    setShowEditAllProducts(false);
    const truck = trucks.find(t => t.id === trip.truckId);
    setTruckSearch(truck ? truck.immatriculation : "");
    setSelectedTruck(truck || null);
    setError(null);
    setShowEditTripModal(true);
  };

  const renderProductPicker = (destinationName, selectedList, isEdit = false) => {
    const available = getProductsForDestination(destinationName);
    if (!destinationName) {
      return (
        <p className="trip-pricing-empty">Choisissez une destination pour voir les produits.</p>
      );
    }

    const selectedMap = Object.fromEntries(
      selectedList.map((item) => [item.productId, item])
    );

    // Edit mode: only show already selected products unless user expands the full list
    const showCatalog = !isEdit || showEditAllProducts;
    const rows = showCatalog
      ? available
      : selectedList.map((sel) => {
          const catalog = available.find((a) => a.productId === sel.productId);
          return {
            productId: sel.productId,
            productName: sel.productName,
            price: catalog ? catalog.price : Number(sel.price),
            prixUsine: Number(
              sel.prixUsine != null
                ? sel.prixUsine
                : (catalog?.prixUsine ?? 0)
            ),
          };
        });

    if (!showCatalog && selectedList.length === 0) {
      return (
        <div className="trip-product-picker">
          <p className="trip-pricing-empty">Aucun produit sélectionné pour ce voyage.</p>
          <button
            type="button"
            className="trip-product-expand-btn"
            onClick={() => setShowEditAllProducts(true)}
          >
            Ajouter / modifier des produits
          </button>
        </div>
      );
    }

    if (showCatalog && available.length === 0) {
      return (
        <p className="trip-pricing-empty">
          Aucun tarif pour cette destination. Configurez-les via « Tarifs destinations ».
        </p>
      );
    }

    return (
      <div className="trip-product-picker">
        {rows.map((item) => {
          const selected = selectedMap[item.productId];
          const checked = Boolean(selected);
          return (
            <div
              key={item.productId}
              className={`trip-product-option ${checked ? "selected" : ""}`}
            >
              <label className="trip-product-option-check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleSelectedProduct(item, isEdit)}
                />
                <span className="trip-product-option-name">{item.productName}</span>
              </label>
              {checked ? (
                <div className="trip-product-option-fields">
                  <label className="trip-product-field">
                    <span>Qté</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="trip-product-qty-input"
                      value={selected.quantity ?? ""}
                      onChange={(e) =>
                        updateSelectedProductQuantity(item.productId, e.target.value, isEdit)
                      }
                      placeholder=""
                      aria-label={`Quantité ${item.productName}`}
                    />
                  </label>
                  <label className="trip-product-field">
                    <span>Prix vente</span>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      className="trip-product-price-input"
                      value={selected.price}
                      onChange={(e) =>
                        updateSelectedProductPrice(item.productId, e.target.value, isEdit)
                      }
                      aria-label={`Prix ${item.productName}`}
                    />
                  </label>
                </div>
              ) : (
                <span className="trip-product-option-price">{formatPrice(item.price)}</span>
              )}
            </div>
          );
        })}
        {(() => {
          const totals = computeTripProductTotals(
            selectedList.map((p) => ({
              quantity: p.quantity,
              price: p.price,
              prix_usine: p.prixUsine,
            }))
          );
          if (selectedList.length === 0) return null;
          return (
            <div className="trip-product-margin-preview">
              Marge produits: <strong>{formatMoney(totals.margin)}</strong>
              <span className="trip-product-margin-detail">
                {" "}(vente {formatMoney(totals.revenue)} − usine {formatMoney(totals.cost)})
              </span>
            </div>
          );
        })()}
        {isEdit && !showEditAllProducts && (
          <button
            type="button"
            className="trip-product-expand-btn"
            onClick={() => setShowEditAllProducts(true)}
          >
            Ajouter / modifier des produits
          </button>
        )}
        {isEdit && showEditAllProducts && (
          <button
            type="button"
            className="trip-product-expand-btn"
            onClick={() => setShowEditAllProducts(false)}
          >
            Masquer les autres produits
          </button>
        )}
      </div>
    );
  };

  const toggleTripDetails = (tripId) => {
    setExpandedTrips((prev) => ({
      ...prev,
      [tripId]: !prev[tripId],
    }));
  };

  const toDateKey = (rawDate) => {
    const d = new Date(rawDate);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  };

  const handleDragStart = (e, tripId) => {
    e.dataTransfer.setData("text/plain", tripId);
    e.dataTransfer.effectAllowed = "move";
    setDraggedTripId(tripId);
  };

  const handleDragEnd = () => {
    setDraggedTripId(null);
    setDropTargetDate(null);
  };

  const handleDayDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetDate(date);
  };

  const handleDayDrop = async (e, date) => {
    e.preventDefault();
    e.stopPropagation();
    suppressDayClickRef.current = true;
    setTimeout(() => { suppressDayClickRef.current = false; }, 200);

    const tripId = e.dataTransfer.getData("text/plain");
    setDraggedTripId(null);
    setDropTargetDate(null);

    if (!tripId) return;
    await handleMoveTrip(tripId, date);
  };

  const handleDayClick = (date) => {
    if (suppressDayClickRef.current || draggedTripId) return;
    openAddTripModal(date);
  };

  const handleMoveTrip = async (tripId, newDate) => {
    const trip = trips.find((t) => t.id === tripId);
    if (!trip) return;

    if (toDateKey(trip.rawDate) === newDate) return;

    const newDateObj = new Date(newDate);
    newDateObj.setUTCHours(0, 0, 0, 0);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    if (newDateObj < today) {
      setError("Impossible de déplacer un voyage vers une date passée.");
      return;
    }

    try {
      setMovingTripId(tripId);
      setError(null);
      const formattedDate = newDateObj.toLocaleDateString("fr-FR");
      const { error } = await withRetry(() =>
        supabase
          .from("trips")
          .update({ date: newDate })
          .eq("id", tripId)
      );
      if (error) throw error;

      setTrips((prev) =>
        prev.map((t) =>
          t.id === tripId ? { ...t, rawDate: newDate, date: formattedDate } : t
        )
      );

      if (!maxDate || newDateObj > maxDate) {
        setMaxDate(newDateObj);
      }

      setSuccessMessage("Voyage déplacé avec succès !");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error moving trip:", err);
      setError("Erreur lors du déplacement du voyage.");
    } finally {
      setMovingTripId(null);
    }
  };

  const getDisplayedDays = () => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const startDate = new Date(today);
    startDate.setUTCDate(today.getUTCDate() + page * 4);

    const days = [];
    for (let i = 0; i < 4; i++) {
      const day = new Date(startDate);
      day.setUTCDate(startDate.getUTCDate() + i);
      if (day >= today) {
        const year = day.getUTCFullYear();
        const month = String(day.getUTCMonth() + 1).padStart(2, '0');
        const dayOfMonth = String(day.getUTCDate()).padStart(2, '0');
        days.push(`${year}-${month}-${dayOfMonth}`);
      }
    }

    if (page === 0 && days.length < 4) {
      const remainingDays = 4 - days.length;
      for (let i = 1; i <= remainingDays; i++) {
        const nextDay = new Date(today);
        nextDay.setUTCDate(today.getUTCDate() + days.length + i - 1);
        const year = nextDay.getUTCFullYear();
        const month = String(nextDay.getUTCMonth() + 1).padStart(2, '0');
        const dayOfMonth = String(nextDay.getUTCDate()).padStart(2, '0');
        days.push(`${year}-${month}-${dayOfMonth}`);
      }
    }

    return days;
  };

  const matchesTripSearch = (trip, query) => {
    if (!query) return true;
    const truck = trucks.find((t) => t.id === trip.truckId);
    const truckLabel = truck?.immatriculation || "non assigné";
    const colorLabel = colorOptions.find((o) => o.value === trip.color)?.label || "";
    const searchable = [
      trip.destination,
      trip.cargo,
      trip.description,
      trip.date,
      truckLabel,
      colorLabel,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchable.includes(query);
  };

  const applyAgendaFilters = (tripList) => {
    const query = tripSearchQuery.trim().toLowerCase();
    return tripList.filter((trip) => {
      if (unassignedOnlyFilter && trip.truckId) return false;
      return matchesTripSearch(trip, query);
    });
  };

  const getGroupedTrips = () => {
    const displayedDays = getDisplayedDays();
    if (displayedDays.length === 0) return {};

    const startDate = new Date(displayedDays[0]);
    startDate.setUTCHours(0, 0, 0, 0);
    const endDate = new Date(displayedDays[displayedDays.length - 1]);
    endDate.setUTCHours(23, 59, 59, 999);

    const sortedTrips = [...trips].sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
    const dateRangeTrips = sortedTrips.filter((trip) => {
      const tripDate = new Date(trip.rawDate);
      tripDate.setUTCHours(0, 0, 0, 0);
      return tripDate >= startDate && tripDate <= endDate;
    });
    const filteredTrips = applyAgendaFilters(dateRangeTrips);

    const groupedByDate = filteredTrips.reduce((acc, trip) => {
      const tripDate = new Date(trip.rawDate);
      const date = `${tripDate.getUTCFullYear()}-${String(tripDate.getUTCMonth() + 1).padStart(2, '0')}-${String(tripDate.getUTCDate()).padStart(2, '0')}`;
      if (!acc[date]) acc[date] = [];
      acc[date].push(trip);
      return acc;
    }, {});

    const colorPriority = {
      '#B91C1C': 1,
      '#1E3A8A': 2,
    };

    Object.keys(groupedByDate).forEach((date) => {
      groupedByDate[date].sort((a, b) => {
        const aPriority = colorPriority[a.color] || 3;
        const bPriority = colorPriority[b.color] || 3;
        return aPriority - bPriority;
      });
    });

    return groupedByDate;
  };

  const handlePrevDays = () => {
    if (page > 0) {
      setPage(prev => prev - 1);
    }
  };

  const handleNextDays = () => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const nextStartDate = new Date(today);
    nextStartDate.setUTCDate(today.getUTCDate() + (page + 1) * 4);
    if (!maxDate || nextStartDate <= maxDate) {
      setPage(prev => prev + 1);
    }
  };

  const groupedTrips = getGroupedTrips();
  const displayedDays = getDisplayedDays();
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const nextStartDate = new Date(today);
  nextStartDate.setUTCDate(today.getUTCDate() + (page + 1) * 4);
  const canGoPrev = page > 0;
  const canGoNext = maxDate && nextStartDate <= maxDate;
  const isFiltering = tripSearchQuery.trim() || unassignedOnlyFilter;
  const filteredAgendaCount = Object.values(groupedTrips).reduce((sum, dayTrips) => sum + dayTrips.length, 0);

  const renderTripCard = (trip) => {
    const truck = trucks.find((t) => t.id === trip.truckId);
    const isExpanded = expandedTrips[trip.id] || false;
    const isDragging = draggedTripId === trip.id;
    const isMoving = movingTripId === trip.id;

    return (
      <div
        key={trip.id}
        className={`trip-scheduler-item trip-scheduler-item-${trip.status} ${isExpanded ? "trip-scheduler-item-expanded" : ""} ${isDragging ? "trip-scheduler-item-dragging" : ""} ${isMoving ? "trip-scheduler-item-moving" : ""}`}
        style={{ backgroundColor: trip.color }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="trip-scheduler-item-header">
          <span
            className="trip-scheduler-drag-handle"
            draggable
            onDragStart={(e) => handleDragStart(e, trip.id)}
            onDragEnd={handleDragEnd}
            title="Glisser pour déplacer"
            aria-label="Glisser pour déplacer"
          >
            ⠿
          </span>
          <div className="trip-scheduler-item-info">
            <h3>{trip.cargo}</h3>
            <p><strong>Camion:</strong> {truck?.immatriculation || "Non assigné"}</p>
          </div>
          <button
            className="trip-scheduler-toggle-btn"
            onClick={() => toggleTripDetails(trip.id)}
            aria-label={isExpanded ? "Réduire les détails" : "Afficher les détails"}
          >
            {isExpanded ? "▲" : "▼"}
          </button>
        </div>
        {isExpanded && (
          <div className="trip-scheduler-item-details">
            <p><strong>Destination:</strong> {trip.destination}</p>
            <p><strong>Date:</strong> {trip.date}</p>
            <div className="trip-product-prices-block">
              <strong>Produits / Tarifs:</strong>
              {trip.productPrices?.length > 0 ? (
                <>
                  <ul>
                    {trip.productPrices.map((item, idx) => (
                      <li key={`${item.product_id || item.product_name}-${idx}`}>
                        {item.product_name}
                        {item.quantity != null && (
                          <> × {formatPrice(item.quantity)}</>
                        )}
                        : {formatPrice(item.price)}
                        {item.prix_usine != null && (
                          <span className="trip-product-usine-inline">
                            {" "}(usine: {formatPrice(item.prix_usine)})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {(() => {
                    const totals = computeTripProductTotals(trip.productPrices);
                    return (
                      <p className="trip-product-margin-card">
                        Marge: {formatMoney(totals.margin)}
                      </p>
                    );
                  })()}
                </>
              ) : (
                <span> Aucun</span>
              )}
            </div>
            <p><strong>Description:</strong> {trip.description || "Aucune description"}</p>
            <p>
              <strong>Statut:</strong>{" "}
              {trip.status === "scheduled"
                ? "Planifié"
                : trip.status === "in_progress"
                ? "En cours"
                : trip.status === "completed"
                ? "Terminé"
                : "Annulé"}
            </p>
            <div className="trip-scheduler-item-actions">
              {!trip.truckId && trip.status !== "canceled" && (
                <button
                  className="trip-scheduler-assign-btn"
                  onClick={() => openAssignTruckModal(trip.id)}
                >
                  🚚 Assigner un Camion
                </button>
              )}
              {trip.status !== "canceled" && trip.status !== "completed" && (
                <>
                  <button
                    className="trip-scheduler-edit-btn"
                    onClick={() => openEditTripModal(trip)}
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    className="trip-scheduler-cancel-trip-btn"
                    onClick={() => handleCancel(trip.id)}
                  >
                    ❌ Annuler
                  </button>
                  <button
                    className="trip-scheduler-complete-btn"
                    onClick={() => handleComplete(trip.id)}
                  >
                    ✅ Terminer
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="trip-scheduler-container">
      <aside className="sidebar">
        <h2 className="trip-scheduler-fleet-title">Système de Gestion & Contrôle</h2>
        <nav>
          <ul>
            <li><Link to="/fleet/dashboard">📊 Gestion de Flotte</Link></li>
            <li><Link to="/cash-tracking">💵 Gestion de Caisse</Link></li>
            <li><Link to="/parc">🔧 Gestion des Pièces</Link></li>
            <li><Link to="/fleet/stock-carburant">⛽ Stock Carburant</Link></li>
            <li><Link to="/stock">📦 Gestion de Stock</Link></li>
            <li className="active"><Link to="/schedule">🗓️ Gestion des Programmes</Link></li>
            <li><Link to="/schedule/history">📜 Historique voyages</Link></li>
            <li><Link to="/schedule/profitability">📈 Rentabilité voyages</Link></li>
            <li><Link to="/maintenance">🛠️ Maintenance</Link></li>
            <li><Link to="/trailers">🚛 Gestion des Remorques</Link></li>
            <li><Link to="/incidents">🚨 Gestion des Incidents</Link></li>
            <li><Link to="/driver-payments">💰 Gestion des salaires </Link></li>
            <li>
                          <Link to="/chatbot">🤖 Système de Reporting</Link>
                        </li>
          </ul>
        </nav>
        <div className="trip-scheduler-sidebar-footer">
          <p>Version 1.2.0</p>
          <p>© 2025</p>
        </div>
      </aside>

      <main className="trip-scheduler-content">
        <header className="trip-scheduler-header">
          <div className="trip-scheduler-header-titles">
            <h1>🗓️ Planifier un Programme</h1>
            <p className="trip-scheduler-header-note">
              Actualisez les données environ toutes les 15 minutes.
            </p>
          </div>
          <div className="trip-scheduler-header-actions">
            <button className="trip-scheduler-refresh-btn" type="button" onClick={fetchData}>
              🔄 Actualiser
            </button>
            <Link to="/schedule/history" className="trip-scheduler-settings-btn">
              📜 Historique
            </Link>
            <Link to="/schedule/profitability" className="trip-scheduler-settings-btn">
              📈 Rentabilité
            </Link>
            <button
              className="trip-scheduler-settings-btn"
              type="button"
              onClick={() => setShowPricingSettings(true)}
            >
              ⚙️ Tarifs destinations
            </button>
            <button className="trip-scheduler-add-btn" type="button" onClick={() => openAddTripModal()}>
              ➕ Planifier un Voyage
            </button>
          </div>
        </header>

        {successMessage && (
          <div className="trip-scheduler-success-message">{successMessage}</div>
        )}
        {error && <div className="trip-scheduler-error-message">{error}</div>}

        <section className="trip-scheduler-list-section">
          <div className="trip-scheduler-nav-controls">
            <div>
              <h2>Voyages Planifiés</h2>
              <p className="trip-scheduler-drag-hint">
                Glissez un voyage vers un autre jour pour le reprogrammer
              </p>
            </div>
            <div className="trip-scheduler-nav-arrows">
              <button
                className="trip-scheduler-nav-btn"
                onClick={handlePrevDays}
                disabled={!canGoPrev}
              >
                ← Précédent
              </button>
              <button
                className="trip-scheduler-nav-btn"
                onClick={handleNextDays}
                disabled={!canGoNext}
              >
                Suivant →
              </button>
            </div>
          </div>

          <div className="trip-scheduler-filters">
            <div className="trip-scheduler-search-wrapper">
              <input
                type="text"
                className="trip-scheduler-search-input"
                placeholder="Rechercher par destination, cargaison, camion..."
                value={tripSearchQuery}
                onChange={(e) => setTripSearchQuery(e.target.value)}
                aria-label="Rechercher des voyages"
              />
              {tripSearchQuery && (
                <button
                  type="button"
                  className="trip-scheduler-search-clear"
                  onClick={() => setTripSearchQuery("")}
                  aria-label="Effacer la recherche"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              type="button"
              className={`trip-scheduler-filter-chip ${unassignedOnlyFilter ? "active" : ""}`}
              onClick={() => setUnassignedOnlyFilter((prev) => !prev)}
            >
              🚚 Non assignés seulement
            </button>
            {isFiltering && (
              <span className="trip-scheduler-filter-count">
                {filteredAgendaCount} voyage{filteredAgendaCount !== 1 ? "s" : ""} affiché{filteredAgendaCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loading ? (
            <div className="trip-scheduler-loading">Chargement des voyages...</div>
          ) : (
            <div className="trip-scheduler-agenda">
              <div className="trip-scheduler-agenda-container">
                {displayedDays.map((date) => {
                  const dayTripCount = groupedTrips[date]?.length || 0;
                  const isDropTarget = dropTargetDate === date && draggedTripId;
                  return (
                  <div
                    key={date}
                    className={`trip-scheduler-agenda-day ${isDropTarget ? "trip-scheduler-agenda-day-drag-over" : ""}`}
                    onClick={() => handleDayClick(date)}
                    onDragOver={(e) => handleDayDragOver(e, date)}
                    onDragLeave={() => setDropTargetDate(null)}
                    onDrop={(e) => handleDayDrop(e, date)}
                  >
                    <h3 className="trip-scheduler-agenda-date">
                      {new Date(date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      {dayTripCount > 0 && (
                        <span className="trip-scheduler-day-count">{dayTripCount}</span>
                      )}
                    </h3>
                    <div className="trip-scheduler-items">
                      {groupedTrips[date]?.length > 0 ? (
                        groupedTrips[date].map((trip) => renderTripCard(trip))
                      ) : (
                        <div className={`trip-scheduler-no-trips ${isDropTarget ? "trip-scheduler-no-trips-drag-over" : ""}`}>
                          {isDropTarget
                            ? "Déposer ici"
                            : isFiltering
                            ? "Aucun voyage correspondant"
                            : "Aucun voyage pour ce jour"}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {showAddTripModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddTripModal(false);
                  setFormData({ truckId: "", destination: "", date: "", cargo: "", description: "", status: "scheduled", color: "#1E3A8A" });
                  setSelectedProducts([]);
                  setTruckSearch("");
                  setSelectedTruck(null);
                  setError(null);
                }}
              >
                
                X
              </button>
              <section className="trip-scheduler-form-section">
                <h2>Planifier un Nouveau Voyage</h2>
                <form onSubmit={handleSubmit} className="trip-scheduler-form">
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="truckSelect">Camion (optionnel)</label>
                    <select
                      id="truckSelect"
                      name="truckId"
                      value={formData.truckId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const truck = trucks.find(t => t.id === selectedId);
                        if (truck) {
                          setFormData(prev => ({ ...prev, truckId: truck.id }));
                          setTruckSearch(truck.immatriculation);
                          setSelectedTruck(truck);
                        } else {
                          setFormData(prev => ({ ...prev, truckId: "" }));
                          setTruckSearch("");
                          setSelectedTruck(null);
                        }
                      }}
                    >
                      <option value="">-- Aucun camion --</option>
                      {trucks.map(truck => (
                        <option key={truck.id} value={truck.id}>
                          {truck.immatriculation}
                        </option>
                      ))}
                    </select>
                    <div style={{ marginTop: '8px' }}>
                      <label htmlFor="truckSearch">Recherche rapide:</label>
                      <div style={{ display: 'flex', position: 'relative' }}>
                        <input
                          type="text"
                          id="truckSearch"
                          value={truckSearch}
                          onChange={(e) => {
                            setTruckSearch(e.target.value);
                            if (selectedTruck && e.target.value !== selectedTruck.immatriculation) {
                              setSelectedTruck(null);
                              setFormData(prev => ({ ...prev, truckId: "" }));
                            }
                          }}
                          placeholder="Filtrer par immatriculation..."
                          autoComplete="off"
                          onFocus={() => {
                            if (truckSearch && !selectedTruck) {
                              setShowTruckList(true);
                            }
                          }}
                        />
                        {showTruckList && truckSearch && filteredTrucks.length > 0 && (
                          <div className="trip-scheduler-truck-list" ref={truckListRef}>
                            {filteredTrucks.map((truck) => (
                              <div
                                key={truck.id}
                                className="trip-scheduler-truck-item"
                                onClick={() => handleTruckSelect(truck)}
                              >
                                {truck.immatriculation}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedTruck && (
                      <div className="trip-scheduler-selected-truck">
                        Camion sélectionné: {selectedTruck.immatriculation}
                      </div>
                    )}
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="destination">Destination <span className="required-field">*</span></label>
                    <select
                      id="destination"
                      name="destination"
                      value={formData.destination}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">-- Choisir une destination --</option>
                      {scheduleDestinations.map((destination) => (
                        <option key={destination.id} value={destination.name}>
                          {destination.name}
                        </option>
                      ))}
                    </select>
                    {scheduleDestinations.length === 0 && (
                      <small className="trip-pricing-empty">
                        Aucune destination configurée. Ouvrez « Tarifs destinations ».
                      </small>
                    )}
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label>Produits / Tarifs</label>
                    {renderProductPicker(formData.destination, selectedProducts, false)}
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="date">Date du Voyage <span className="required-field">*</span></label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="cargo">Cargaison <span className="required-field">*</span></label>
                    <input
                      type="text"
                      id="cargo"
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleInputChange}
                      placeholder="ex: Ciment, 5 tonnes"
                      required
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Notes ou instructions (pas les tarifs)"
                      rows="4"
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="status">Statut</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="scheduled">Planifié</option>
                      <option value="in_progress">En cours</option>
                      <option value="completed">Terminé</option>
                    </select>
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="color">Couleur de la carte</label>
                    <ColorPicker
                      value={formData.color}
                      onSelect={(color) => setFormData((prev) => ({ ...prev, color }))}
                    />
                    <span className="trip-scheduler-color-label">
                      {colorOptions.find((o) => o.value === formData.color)?.label || "Gris"}
                    </span>
                  </div>
                  <div className="trip-scheduler-form-actions">
                    <button type="submit" className="trip-scheduler-save-btn">
                      Planifier
                    </button>
                    <button
                      type="button"
                      className="trip-scheduler-cancel-btn"
                      onClick={() => {
                        setFormData({ truckId: "", destination: "", date: "", cargo: "", description: "", status: "scheduled", color: "#1E3A8A" });
                        setSelectedProducts([]);
                        setTruckSearch("");
                        setSelectedTruck(null);
                      }}
                    >
                      Effacer le Formulaire
                    </button>
                  </div>
                </form>
              </section>
            </div>
          </div>
        )}

        {showAssignTruckModal && (
          <div className="modal-overlay">
            <div className="trip-scheduler-modal-content">
              <button
                className="trip-scheduler-modal-close"
                onClick={() => {
                  setShowAssignTruckModal(false);
                  setAssignFormData({ tripId: "", truckId: "" });
                  setTruckSearch("");
                  setSelectedTruck(null);
                  setError(null);
                }}
              >
                ✕
              </button>
              <section className="trip-scheduler-form-section">
                <h2>Assigner un Camion</h2>
                <form onSubmit={handleAssignTruck} className="trip-scheduler-form">
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="assignTruckSelect">Camion <span className="required-field">*</span></label>
                    <select
                      id="assignTruckSelect"
                      name="truckId"
                      value={assignFormData.truckId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const truck = trucks.find(t => t.id === selectedId);
                        if (truck) {
                          setAssignFormData(prev => ({ ...prev, truckId: truck.id }));
                          setTruckSearch(truck.immatriculation);
                          setSelectedTruck(truck);
                        } else {
                          setAssignFormData(prev => ({ ...prev, truckId: "" }));
                          setTruckSearch("");
                          setSelectedTruck(null);
                        }
                      }}
                      required
                    >
                      <option value="">-- Sélectionnez un camion --</option>
                      {trucks.map(truck => (
                        <option key={truck.id} value={truck.id}>
                          {truck.immatriculation}
                        </option>
                      ))}
                    </select>
                    <div style={{ marginTop: '8px' }}>
                      <label htmlFor="truckSearchAssign">Recherche rapide:</label>
                      <div style={{ display: 'flex', position: 'relative' }}>
                        <input
                          type="text"
                          id="truckSearchAssign"
                          value={truckSearch}
                          onChange={(e) => {
                            setTruckSearch(e.target.value);
                            if (selectedTruck && e.target.value !== selectedTruck.immatriculation) {
                              setSelectedTruck(null);
                              setAssignFormData(prev => ({ ...prev, truckId: "" }));
                            }
                          }}
                          placeholder="Filtrer par immatriculation..."
                          autoComplete="off"
                          onFocus={() => {
                            if (truckSearch && !selectedTruck) {
                              setShowTruckList(true);
                            }
                          }}
                        />
                        {showTruckList && truckSearch && filteredTrucks.length > 0 && (
                          <div className="trip-scheduler-truck-list" ref={truckListRef}>
                            {filteredTrucks.map((truck) => (
                              <div
                                key={truck.id}
                                className="trip-scheduler-truck-item"
                                onClick={() => handleTruckSelect(truck, true)}
                              >
                                {truck.immatriculation}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedTruck && (
                      <div className="trip-scheduler-selected-truck">
                        Camion sélectionné: {selectedTruck.immatriculation}
                      </div>
                    )}
                  </div>
                  <div className="trip-scheduler-form-actions">
                    <button type="submit" className="trip-scheduler-save-btn">
                      Assigner
                    </button>
                    <button
                      type="button"
                      className="trip-scheduler-cancel-btn"
                      onClick={() => {
                        setShowAssignTruckModal(false);
                        setAssignFormData({ tripId: "", truckId: "" });
                        setTruckSearch("");
                        setSelectedTruck(null);
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </section>
            </div>
          </div>
        )}

        {showEditTripModal && (
          <div className="modal-overlay">
            <div className="trip-scheduler-modal-content">
              <button
                className="trip-scheduler-modal-close"
                onClick={() => {
                  setShowEditTripModal(false);
                  setEditFormData({ id: "", truckId: "", destination: "", date: "", cargo: "", description: "", status: "scheduled", color: "#1E3A8A" });
                  setEditSelectedProducts([]);
                  setShowEditAllProducts(false);
                  setTruckSearch("");
                  setSelectedTruck(null);
                  setError(null);
                }}
              >
                ✕
              </button>
              <section className="trip-scheduler-form-section">
                <h2>Modifier le Voyage</h2>
                <form onSubmit={handleEditSubmit} className="trip-scheduler-form">
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="editTruckSelect">Camion (optionnel)</label>
                    <select
                      id="editTruckSelect"
                      name="truckId"
                      value={editFormData.truckId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const truck = trucks.find(t => t.id === selectedId);
                        if (truck) {
                          setEditFormData(prev => ({ ...prev, truckId: truck.id }));
                          setTruckSearch(truck.immatriculation);
                          setSelectedTruck(truck);
                        } else {
                          setEditFormData(prev => ({ ...prev, truckId: "" }));
                          setTruckSearch("");
                          setSelectedTruck(null);
                        }
                      }}
                    >
                      <option value="">-- Aucun camion --</option>
                      {trucks.map(truck => (
                        <option key={truck.id} value={truck.id}>
                          {truck.immatriculation}
                        </option>
                      ))}
                    </select>
                    <div style={{ marginTop: '8px' }}>
                      <label htmlFor="truckSearchEdit">Recherche rapide:</label>
                      <div style={{ display: 'flex', position: 'relative' }}>
                        <input
                          type="text"
                          id="truckSearchEdit"
                          value={truckSearch}
                          onChange={(e) => {
                            setTruckSearch(e.target.value);
                            if (selectedTruck && e.target.value !== selectedTruck.immatriculation) {
                              setSelectedTruck(null);
                              setEditFormData(prev => ({ ...prev, truckId: "" }));
                            }
                          }}
                          placeholder="Filtrer par immatriculation..."
                          autoComplete="off"
                          onFocus={() => {
                            if (truckSearch && !selectedTruck) {
                              setShowTruckList(true);
                            }
                          }}
                        />
                        {showTruckList && truckSearch && filteredTrucks.length > 0 && (
                          <div className="trip-scheduler-truck-list" ref={truckListRef}>
                            {filteredTrucks.map((truck) => (
                              <div
                                key={truck.id}
                                className="trip-scheduler-truck-item"
                                onClick={() => handleTruckSelect(truck, false, true)}
                              >
                                {truck.immatriculation}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedTruck && (
                      <div className="trip-scheduler-selected-truck">
                        Camion sélectionné: {selectedTruck.immatriculation}
                      </div>
                    )}
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="editDestination">Destination <span className="required-field">*</span></label>
                    <select
                      id="editDestination"
                      name="destination"
                      value={editFormData.destination}
                      onChange={handleEditInputChange}
                      required
                    >
                      <option value="">-- Choisir une destination --</option>
                      {scheduleDestinations.map((destination) => (
                        <option key={destination.id} value={destination.name}>
                          {destination.name}
                        </option>
                      ))}
                      {editFormData.destination &&
                        !scheduleDestinations.some(
                          (d) => d.name.toLowerCase() === editFormData.destination.toLowerCase()
                        ) && (
                          <option value={editFormData.destination}>
                            {editFormData.destination} (ancienne)
                          </option>
                        )}
                    </select>
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label>Produits / Tarifs</label>
                    {renderProductPicker(editFormData.destination, editSelectedProducts, true)}
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="editDate">Date du Voyage <span className="required-field">*</span></label>
                    <input
                      type="date"
                      id="editDate"
                      name="date"
                      value={editFormData.date}
                      onChange={handleEditInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="editCargo">Cargaison <span className="required-field">*</span></label>
                    <input
                      type="text"
                      id="editCargo"
                      name="cargo"
                      value={editFormData.cargo}
                      onChange={handleEditInputChange}
                      placeholder="ex: Ciment, 5 tonnes"
                      required
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="editDescription">Description</label>
                    <textarea
                      id="editDescription"
                      name="description"
                      value={editFormData.description}
                      onChange={handleEditInputChange}
                      placeholder="Notes ou instructions (pas les tarifs)"
                      rows="4"
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="editStatus">Statut</label>
                    <select
                      id="editStatus"
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditInputChange}
                    >
                      <option value="scheduled">Planifié</option>
                      <option value="in_progress">En cours</option>
                      <option value="completed">Terminé</option>
                    </select>
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="editColor">Couleur de la carte</label>
                    <ColorPicker
                      value={editFormData.color}
                      onSelect={(color) => setEditFormData((prev) => ({ ...prev, color }))}
                    />
                    <span className="trip-scheduler-color-label">
                      {colorOptions.find((o) => o.value === editFormData.color)?.label || "Gris"}
                    </span>
                  </div>
                  <div className="trip-scheduler-form-actions">
                    <button type="submit" className="trip-scheduler-save-btn">
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      className="trip-scheduler-cancel-btn"
                      onClick={() => {
                        setShowEditTripModal(false);
                        setEditFormData({ id: "", truckId: "", destination: "", date: "", cargo: "", description: "", status: "scheduled", color: "#1E3A8A" });
                        setEditSelectedProducts([]);
                        setShowEditAllProducts(false);
                        setTruckSearch("");
                        setSelectedTruck(null);
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              </section>
            </div>
          </div>
        )}

        <TripPricingSettings
          open={showPricingSettings}
          onClose={() => setShowPricingSettings(false)}
          onCatalogChanged={fetchPricingCatalog}
        />
      </main>
    </div>
  );
};

export default TripScheduler;